import React, { useContext } from "react";
import { AppContext, BookingData } from "../../../AppDataProvider/AppDataProvider";
import { corners, dateTimeFormats, gap, margin, marginBottom, marginRight, marginY, padding, typography } from "../../../common/styles";
import { getCurrencyDisplay, getDurationDisplay, getTZAbbreviation, requireBookingDataFields } from "../../../utils";
import { faCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { addMinutes } from "date-fns";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Pill } from "../../../common/components";
import { PartRequired } from "../../../utils/types";
import CustomerTranslations from "../../../utils/CustomerTranslations";
export type SummaryBookingData = PartRequired<BookingData, "duration" | "organisation" | "payNow" | "service" | "startDate" | "timezone">;
export function Summary() {
  const {
    i18n,
    t
  } = useTranslation();
  const {
    translateEntity
  } = CustomerTranslations.useCustomerTranslation();
  const {
    appData
  } = useContext(AppContext);
  const {
    bookingData
  } = appData;
  requireBookingDataFields<SummaryBookingData>(bookingData, ["duration", "organisation", "service", "startDate", "timezone"]);
  const {
    duration,
    organisation,
    resource,
    selectedBookings,
    service,
    startDate,
    timezone,
    totalPaid,
    payNow
  } = bookingData;
  const {
    currency
  } = organisation;

  // If booking onto an existing booking where the booking time has been changed
  // and its duration no longer matches duration on service, then use start and
  // end time of booking to calculate duration
  let calculatedDuration = duration;
  if (selectedBookings && selectedBookings.length === 1) {
    const [selectedBooking] = selectedBookings;
    const {
      start,
      end
    } = selectedBooking;
    const startTime = new Date(start);
    const endTime = new Date(end);
    const durationInMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60);
    calculatedDuration = {
      minutes: durationInMinutes
    };
  }
  const timezoneAbbreviation = getTZAbbreviation({
    date: startDate,
    locale: i18n.language,
    timezone
  });
  return <Container data-sentry-element="Container" data-sentry-component="Summary" data-sentry-source-file="Summary.tsx">
      <TitleRow data-sentry-element="TitleRow" data-sentry-source-file="Summary.tsx">
        {translateEntity({
        document: service,
        type: "service"
      }).name}
      </TitleRow>
      <ResourceOrgRow data-sentry-element="ResourceOrgRow" data-sentry-source-file="Summary.tsx">
        {resource && <>
            <Text>
              {translateEntity({
            document: resource,
            type: "resource"
          }).name}
            </Text>
            <DotSeparator />
          </>}
        <Text data-sentry-element="Text" data-sentry-source-file="Summary.tsx">
          {translateEntity({
          document: organisation,
          type: "account"
        }).name}
        </Text>
      </ResourceOrgRow>
      <Row data-sentry-element="Row" data-sentry-source-file="Summary.tsx">
        <Text data-sentry-element="Text" data-sentry-source-file="Summary.tsx">
          {startDate.toLocaleString(i18n.language, {
          ...dateTimeFormats.date.default,
          timeZone: timezone
        })}
        </Text>
      </Row>
      <Row data-sentry-element="Row" data-sentry-source-file="Summary.tsx">
        <Text data-sentry-element="Text" data-sentry-source-file="Summary.tsx">
          {startDate.toLocaleString(i18n.language, {
          ...dateTimeFormats.time.default,
          timeZone: timezone
        })}
          {" – "}
          {addMinutes(startDate, calculatedDuration.minutes).toLocaleTimeString(i18n.language, {
          ...dateTimeFormats.time.default,
          timeZone: timezone
        })}
        </Text>
        <GreyText data-sentry-element="GreyText" data-sentry-source-file="Summary.tsx">{timezoneAbbreviation}</GreyText>
      </Row>
      <Row data-sentry-element="Row" data-sentry-source-file="Summary.tsx">
        <GreyText data-sentry-element="GreyText" data-sentry-source-file="Summary.tsx">{t("duration", {
          count: 1
        })}</GreyText>
        <Text data-sentry-element="Text" data-sentry-source-file="Summary.tsx">{getDurationDisplay(calculatedDuration, t)}</Text>
      </Row>
      {payNow && <TotalRow>
          <TotalLabel>{t("totalPaid")}</TotalLabel>
          <Pill small>
            {getCurrencyDisplay(totalPaid ?? 0, currency, i18n.language)}
          </Pill>
        </TotalRow>}
    </Container>;
}
const Container = styled.div`
  width: 100%;
  ${marginBottom.m};
  border: 1px solid ${({
  theme
}) => theme.brand.cssVars["--d-border"]};
  border-top: 4px solid ${({
  theme
}) => theme.brand.cssVars["--d-accent"]};
  ${corners.m};
  ${padding.m};
  background: ${({
  theme
}) => theme.brand.cssVars["--d-card"]};
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};
`;
const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  ${gap.s}
  ${marginBottom.xs};
  text-align: left;
`;
const ResourceOrgRow = styled(Row)`
  display: flex;
  flex-direction: row;
  ${gap.s}
`;
const Text = styled.span`
  ${typography.small};

  &::first-letter {
    text-transform: capitalize;
  }
`;
const GreyText = styled(Text)`
  color: ${({
  theme
}) => theme.brand.cssVars["--d-muted"]};
`;
const TitleRow = styled(Row)`
  ${typography.smallBold}
`;
const DotSeparator = styled(FontAwesomeIcon).attrs({
  icon: faCircle
})`
  height: 0.25rem;
  width: 0.25rem;
  ${marginRight.s};
  ${marginY.xs}
  color: ${({
  theme
}) => theme.brand.cssVars["--d-muted"]};
`;
const TotalRow = styled(Row)`
  justify-content: space-between;
  ${marginBottom.zero};
`;
const TotalLabel = styled(GreyText)`
  ${margin.zero}
`;
export default Summary;